import React from 'react';

/**
 * Escapa caracteres especiais de HTML para prevenir injeções de script (XSS).
 */
function escapeHtml(str: string): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Parser de abertura de tag <a ...> para extrair atributos de forma sequencial.
 * - Comentário Obrigatório: href é lido apenas dos atributos da abertura, nunca do corpo.
 * - Respeita aspas simples e duplas (caractere '>' dentro de aspas não encerra a tag).
 * - Identifica exatamente o nome de atributo 'href', ignorando data-href, x-href ou valores de outros atributos (como title="href=...").
 */
function parseOpeningTagAttributes(tagStr: string): { openingTagLength: number; attributes: Record<string, string> } | null {
  const trimmed = tagStr.trim();
  if (!/^<a\b/i.test(trimmed)) return null;

  let i = 2; // Após "<a"
  const len = trimmed.length;
  let inQuote: '"' | "'" | null = null;
  let closingAngleIndex = -1;

  for (; i < len; i++) {
    const char = trimmed[i];
    if (inQuote) {
      if (char === inQuote) {
        inQuote = null;
      }
    } else {
      if (char === '"' || char === "'") {
        inQuote = char;
      } else if (char === '>') {
        closingAngleIndex = i;
        break;
      }
    }
  }

  if (closingAngleIndex === -1) return null; // Tag de abertura malformada/incompleta

  const openingContent = trimmed.slice(2, closingAngleIndex);
  const attributes: Record<string, string> = {};

  let attrIdx = 0;
  const attrLen = openingContent.length;

  while (attrIdx < attrLen) {
    while (attrIdx < attrLen && /\s/.test(openingContent[attrIdx])) {
      attrIdx++;
    }
    if (attrIdx >= attrLen) break;

    if (openingContent[attrIdx] === '/') {
      attrIdx++;
      continue;
    }

    const nameStart = attrIdx;
    while (attrIdx < attrLen) {
      const char = openingContent[attrIdx];
      if (/\s/.test(char) || char === '=' || char === '>') {
        break;
      }
      if (char === '/' && attrIdx + 1 < attrLen && openingContent[attrIdx + 1] === '>') {
        break;
      }
      attrIdx++;
    }

    const attrName = openingContent.slice(nameStart, attrIdx).toLowerCase();
    if (!attrName) break;

    while (attrIdx < attrLen && /\s/.test(openingContent[attrIdx])) {
      attrIdx++;
    }

    let attrValue = '';
    if (attrIdx < attrLen && openingContent[attrIdx] === '=') {
      attrIdx++; // Pula '='

      while (attrIdx < attrLen && /\s/.test(openingContent[attrIdx])) {
        attrIdx++;
      }

      if (attrIdx < attrLen) {
        const quoteChar = openingContent[attrIdx];
        if (quoteChar === '"' || quoteChar === "'") {
          attrIdx++;
          const valStart = attrIdx;
          while (attrIdx < attrLen && openingContent[attrIdx] !== quoteChar) {
            attrIdx++;
          }
          attrValue = openingContent.slice(valStart, attrIdx);
          if (attrIdx < attrLen && openingContent[attrIdx] === quoteChar) {
            attrIdx++;
          }
        } else {
          const valStart = attrIdx;
          while (attrIdx < attrLen) {
            const char = openingContent[attrIdx];
            if (/\s/.test(char) || char === '>') {
              break;
            }
            if (char === '/' && attrIdx + 1 < attrLen && openingContent[attrIdx + 1] === '>') {
              break;
            }
            attrIdx++;
          }
          attrValue = openingContent.slice(valStart, attrIdx);
        }
      }
    }

    attributes[attrName] = attrValue;
  }

  return { openingTagLength: closingAngleIndex + 1, attributes };
}

/**
 * Restaura e sanitiza URLs e tags <a> capturadas.
 * - href é lido apenas dos atributos da abertura, nunca do corpo.
 * - Suporta apenas protocolos http:// e https://.
 * - Recontrói a anchor com atributos controlados e texto limpo escapado.
 */
export function restoreSafeLink(originalUrl: string): string {
  if (!originalUrl) return '';

  const trimmed = originalUrl.trim();

  // Se for uma tag <a> capturada
  if (/^<a\b/i.test(trimmed)) {
    // Parser da tag de abertura para ler apenas os atributos legítimos da abertura
    const parsed = parseOpeningTagAttributes(trimmed);
    if (!parsed) {
      // Abertura malformada/inválida vira texto puro escapado
      return escapeHtml(trimmed);
    }

    const rawHref = (parsed.attributes['href'] || '').trim();

    // Extrai o conteúdo interno do corpo da tag (entre > e </a>)
    const bodyContent = trimmed.slice(parsed.openingTagLength).replace(/<\/a\s*>$/i, '');
    const cleanText = escapeHtml(bodyContent.replace(/<[^>]*>/g, '')).trim();

    // Protocolos HTTP/HTTPS somente
    if (/^https?:\/\//i.test(rawHref)) {
      const safeHref = escapeHtml(rawHref);
      const displayText = cleanText || safeHref;
      return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline break-all">${displayText}</a>`;
    }

    // Se não tiver atributo href ou o protocolo for inválido/inseguro, retorna apenas o texto seguro sem tag <a>
    return cleanText;
  }

  // Se for uma URL nua (http:// ou https://)
  if (/^https?:\/\//i.test(trimmed)) {
    const safeUrl = escapeHtml(trimmed);
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline break-all">${safeUrl}</a>`;
  }

  // Fallback seguro: texto escapado
  return escapeHtml(originalUrl);
}

/**
 * Formata texto de mensagem WhatsApp pra renderização:
 * 1. O marcador não pode usar caracteres consumidos pelo parser markdown (*, _, ~).
 * 2. Comentário Obrigatório: Menções não podem operar sobre URLs ou atributos HTML.
 * 3. Comentário Obrigatório: href é lido apenas dos atributos da abertura, nunca do corpo.
 */
export function formatMessageText(text: string, leadName?: string): React.ReactNode {
  if (!text) return null;

  // Sentinelas dinâmicas sem caracteres de markdown (*, _, ~)
  const urlPlaceholders: string[] = [];
  let tokenPrefix = '\uE000URLTOKEN';

  while (text.includes(tokenPrefix)) {
    tokenPrefix += 'X';
  }

  const tokenSuffix = '\uE001';

  // 1. Extrai URLs e tags <a ...>...</a> para tokens collision-safe ANTES de qualquer outro processamento
  // Ordem de preferência: 1. Bloco completo <a ...>...</a> | 2. Tag de abertura <a ...> solta | 3. URLs nuas
  let protectedText = text.replace(/(<a\b[\s\S]*?<\/a>|<a\b[^>]*>|https?:\/\/[^\s<>"'\\]+)/gi, (match, _p1, offset, fullString) => {
    if (/^<a\b/i.test(match.trim())) {
      const idx = urlPlaceholders.length;
      urlPlaceholders.push(match);
      return `${tokenPrefix}${idx}${tokenSuffix}`;
    }

    // Verifica se a URL encontrada está precedida por '=' (ex: href="https://..." em tag malformada)
    const charBefore = typeof offset === 'number' && offset > 0 ? fullString.slice(Math.max(0, offset - 10), offset) : '';
    if (/[=]\s*["']?$/i.test(charBefore)) {
      return match; // Não extrai como URL nua se for valor de atributo de tag não fechada
    }

    // Para URLs nuas, separa pontuações/caracteres de markdown do final se existirem (*, _, ~, ., ,, !, ?, ))
    let cleanUrl = match;
    let trailingPunct = '';

    const punctMatch = cleanUrl.match(/[*_~.,!?)]+$/);
    if (punctMatch) {
      trailingPunct = punctMatch[0];
      cleanUrl = cleanUrl.slice(0, -trailingPunct.length);
    }

    const idx = urlPlaceholders.length;
    urlPlaceholders.push(cleanUrl);
    return `${tokenPrefix}${idx}${tokenSuffix}${trailingPunct}`;
  });

  // 2. Escapa TODO o texto de entrada não confiável
  let escapedText = escapeHtml(protectedText);

  // 3. Converte Markdown do WhatsApp (*negrito*, _itálico_, ~riscado~) sobre o texto escapado
  let converted = escapedText
    .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
    .replace(/_([^_\n]+)_/g, '<em>$1</em>')
    .replace(/~([^~\n]+)~/g, '<del>$1</del>');

  // 4. PIPELINE DE MENÇÕES: Menções não podem operar sobre URLs ou atributos HTML.
  // Processa substituição de @numero por @leadName e estilização de menções SOMENTE sobre o texto protegido.
  let withMentions = converted;
  if (leadName) {
    const safeLeadName = escapeHtml(leadName);
    // Usa callback de função para evitar que caracteres especiais (como $& ou $1) no nome sejam interpretados
    withMentions = withMentions.replace(/@(\d{10,15})/g, () => `@${safeLeadName}`);
  }

  // Estiliza menções (@Nome ou @telefone) com <span> controlado no texto visível
  withMentions = withMentions.replace(/(^|[\s>(])@([a-zA-Z0-9_\u00C0-\u00FF][a-zA-Z0-9_\u00C0-\u00FF\s.-]{0,35}[a-zA-Z0-9_\u00C0-\u00FF]|\d{8,16})/g, (match, prefix, mentionBody) => {
    return `${prefix}<span class="text-blue-500 dark:text-blue-400 font-semibold hover:underline cursor-pointer">@${mentionBody}</span>`;
  });

  // 5. RESTAURAÇÃO DE LINKS POR ÚLTIMO:
  // Restaura os tokens de URLs de forma segura, garantindo integridade total de destinos e atributos.
  const tokenRegex = new RegExp(`${tokenPrefix}(\\d+)${tokenSuffix}`, 'g');
  let finalHtml = withMentions.replace(tokenRegex, (_, tokenIndex) => {
    const originalUrl = urlPlaceholders[Number(tokenIndex)];
    return restoreSafeLink(originalUrl);
  });

  return <span dangerouslySetInnerHTML={{ __html: finalHtml }} />;
}

/**
 * Resolve nome do lead pelo número — busca em cache local.
 * - Comentário Obrigatório: Este helper também roda antes do formatter; não pode modificar
 *   URLs ou atributos, mesmo que o formatter posterior seja seguro.
 */
export function resolveMentionsInText(text: string, leadsByPhone?: Map<string, string>): string {
  if (!text) return text;
  if (!leadsByPhone) return text;

  // Protege URLs e tags <a> existentes para evitar alterar números de telefone presentes no meio das URLs
  const urlPlaceholders: string[] = [];
  let tokenPrefix = '\uE000URLTOKEN';
  while (text.includes(tokenPrefix)) {
    tokenPrefix += 'X';
  }
  const tokenSuffix = '\uE001';

  let protectedText = text.replace(/(<a\b[\s\S]*?<\/a>|<a\b[^>]*>|https?:\/\/[^\s<>"'\\]+)/gi, (match, _p1, offset, fullString) => {
    if (/^<a\b/i.test(match.trim())) {
      const idx = urlPlaceholders.length;
      urlPlaceholders.push(match);
      return `${tokenPrefix}${idx}${tokenSuffix}`;
    }

    const charBefore = typeof offset === 'number' && offset > 0 ? fullString.slice(Math.max(0, offset - 10), offset) : '';
    if (/[=]\s*["']?$/i.test(charBefore)) {
      return match;
    }

    const idx = urlPlaceholders.length;
    urlPlaceholders.push(match);
    return `${tokenPrefix}${idx}${tokenSuffix}`;
  });

  // Substitui telefones por nomes usando callback seguro de função para manter $& ou $1 literais
  // Comentário Importante: O helper retorna o nome literal, sem pré-escapar o HTML, pois o formatMessageText
  // é a real fronteira de escape HTML, prevenindo a dupla escapagem (ex: "Ana & Bia" -> "Ana &amp; Bia").
  let replacedText = protectedText.replace(/@(\d{10,15})/g, (match, phone) => {
    const nome = leadsByPhone.get(phone);
    if (nome) {
      return `@${nome}`;
    }
    return match;
  });

  // Restaura as URLs protegidas
  const tokenRegex = new RegExp(`${tokenPrefix}(\\d+)${tokenSuffix}`, 'g');
  return replacedText.replace(tokenRegex, (_, tokenIndex) => {
    return urlPlaceholders[Number(tokenIndex)];
  });
}
